import { Button, MultipleTextField, TextField, Typography } from "matsuri-ui"
import { ChangeEvent, useCallback, useRef } from "react"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import AddRoundedIcon from "@mui/icons-material/AddRounded"
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import {
    AddTile,
    AddTileIcon,
    BannerLines,
    Card,
    CardTitle,
    CategoryBadge,
    Content,
    InstructionBanner,
    Main,
    PhotoCount,
    PhotoSectionLabel,
    RemoveButton,
    StatusOverlay,
    Thumbnail,
    ThumbnailGrid,
    ThumbnailWrapper,
    TitleAccent,
    WarningBanner
} from "./styled"
import { LostItemMaxImageCount } from "./constants"
import { MobileLayout } from "./utils"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import {
    ImageViewerOverlay,
    useImageViewerOverlay
} from "@/pages/OperationInfoPage/parts/ImageViewerOverlay"
import { ImmediatePhotoItem } from "@/hooks/useImmediatePhotoUpload"
import { LostItemCategory } from "@/hooks/useLostItemReport"
import { useTranslation } from "@/hooks/useTranslation"

export interface CompressingPreview {
    id: string
    objectURL: string
}

interface Props {
    category: LostItemCategory
    itemDescription: string
    storagePlace: string
    note: string
    photos: ImmediatePhotoItem[]
    compressingPreviews: CompressingPreview[]
    canSubmit: boolean
    layout: MobileLayout
    onChangeItemDescription: (value: string) => void
    onChangeStoragePlace: (value: string) => void
    onChangeNote: (value: string) => void
    onFilesSelected: (files: File[]) => void
    onRemovePhoto: (id: string) => void
    onRetryPhoto: (id: string) => void
    onConfirm: () => void
    onBack: () => void
    onClose: () => void
}

/**
 * 忘れ物1件分の詳細入力フォーム。
 * 貴重品は1物品＝1件、一般品は同じ保管場所にまとめた分を1件にできる。
 * 誤ったカテゴリで報告されないよう、粒度の期待値をフォーム上に明示する。
 */
export const ReportFormView = ({
    category,
    itemDescription,
    storagePlace,
    note,
    photos,
    compressingPreviews,
    canSubmit,
    layout,
    onChangeItemDescription,
    onChangeStoragePlace,
    onChangeNote,
    onFilesSelected,
    onRemovePhoto,
    onRetryPhoto,
    onConfirm,
    onBack,
    onClose
}: Props) => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const { openedImage, openImageViewer, closeImageViewer } =
        useImageViewerOverlay()
    const onOpenPhoto = (photo: { id: string; url: string }) =>
        openImageViewer({ id: photo.id, imageUrl: photo.url })

    const isValuable = category === "valuable"
    const photoCount = photos.length + compressingPreviews.length
    const canAddPhoto = photoCount < LostItemMaxImageCount

    const handleFileChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const files = event.currentTarget.files
            if (!files) return
            const fileList = Array.from(files)
            // 同じファイルを連続で選べるよう value をリセットする
            event.currentTarget.value = ""
            onFilesSelected(fileList)
        },
        [onFilesSelected]
    )

    const handleCaptureClick = useCallback(() => {
        fileInputRef.current?.click()
    }, [])

    return (
        <Main style={layout.rootProps.style}>
            <Header {...layout.headerProps}>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">
                            {t("忘れ物を報告")}
                        </Typography>
                    }
                    onPrev={onBack}
                    onNext={onClose}
                    nextIcon={<CloseIcon />}
                />
            </Header>

            <div {...layout.bodyProps}>
                <Content>
                    <CategoryBadge valuable={isValuable}>
                        {isValuable
                            ? t("貴重品として報告します")
                            : t("一般品として報告します")}
                    </CategoryBadge>

                    {/* 報告の粒度は誤りやすいので、カテゴリごとに期待値を明示する */}
                    {isValuable ? (
                        <WarningBanner>
                            <WarningAmberRoundedIcon style={{ fontSize: 20 }} />
                            <BannerLines>
                                <span>{t("1点ずつ報告してください")}</span>
                                <span>
                                    {t(
                                        "例：スマホと財布がある場合は、2回に分けて報告"
                                    )}
                                </span>
                            </BannerLines>
                        </WarningBanner>
                    ) : (
                        <InstructionBanner>
                            <AssignmentRoundedIcon style={{ fontSize: 20 }} />
                            <BannerLines>
                                <span>
                                    {t("同じ場所にまとめた分は1件でOKです")}
                                </span>
                                <span>
                                    {t(
                                        "例：衣類3枚を同じクローゼットに入れた → まとめて1件報告"
                                    )}
                                </span>
                            </BannerLines>
                        </InstructionBanner>
                    )}

                    <Card elevation={1}>
                        <CardTitle>
                            <TitleAccent />
                            {t("報告内容")}
                        </CardTitle>

                        <MultipleTextField
                            name="itemDescription"
                            label={t("物品の説明")}
                            placeholder={
                                isValuable
                                    ? t("例：黒いスマートフォン")
                                    : t("例：グレーのパーカー、Tシャツ2枚")
                            }
                            value={itemDescription}
                            onChange={value =>
                                onChangeItemDescription(value ?? "")
                            }
                            required
                        />

                        <TextField
                            name="storagePlace"
                            label={t("保管場所")}
                            placeholder={t("例：クローゼット上段")}
                            value={storagePlace}
                            onChange={value =>
                                onChangeStoragePlace(value ?? "")
                            }
                            required
                        />

                        <PhotoSectionLabel>
                            {t("保管場所の写真（必須）")}
                            {photoCount > 0 && (
                                <PhotoCount>
                                    {photoCount}
                                    {t("枚")}
                                </PhotoCount>
                            )}
                        </PhotoSectionLabel>

                        <AddTile
                            type="button"
                            disabled={!canAddPhoto}
                            onClick={handleCaptureClick}
                        >
                            <AddTileIcon>
                                <AddRoundedIcon style={{ fontSize: 28 }} />
                            </AddTileIcon>
                            <span>
                                {canAddPhoto
                                    ? t("写真を追加（1〜3枚）")
                                    : t("写真は3枚までです")}
                            </span>
                        </AddTile>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />

                        {photoCount > 0 && (
                            <ThumbnailGrid>
                                {compressingPreviews.map(preview => (
                                    <ThumbnailWrapper key={preview.id}>
                                        <Thumbnail
                                            src={preview.objectURL}
                                            alt=""
                                        />
                                        {/* アップロードはこの後。ここはまだ圧縮している段階 */}
                                        <StatusOverlay>
                                            {t("圧縮中…")}
                                        </StatusOverlay>
                                    </ThumbnailWrapper>
                                ))}
                                {photos.map(photo => (
                                    <ThumbnailWrapper
                                        key={photo.id}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={t("写真を拡大表示")}
                                        css={css`
                                            cursor: pointer;
                                        `}
                                        onClick={() =>
                                            onOpenPhoto({
                                                id: photo.id,
                                                url: photo.objectURL
                                            })
                                        }
                                        onKeyDown={e => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            ) {
                                                e.preventDefault()
                                                onOpenPhoto({
                                                    id: photo.id,
                                                    url: photo.objectURL
                                                })
                                            }
                                        }}
                                    >
                                        <Thumbnail
                                            src={photo.objectURL}
                                            alt=""
                                        />
                                        <RemoveButton
                                            type="button"
                                            aria-label={t("写真を削除")}
                                            // 削除は拡大表示より優先する
                                            onClick={e => {
                                                e.stopPropagation()
                                                onRemovePhoto(photo.id)
                                            }}
                                        >
                                            <CloseIcon
                                                style={{ fontSize: 16 }}
                                            />
                                        </RemoveButton>
                                        {photo.status === "uploading" && (
                                            <StatusOverlay>
                                                {t("アップロード中…")}
                                            </StatusOverlay>
                                        )}
                                        {photo.status === "error" && (
                                            <StatusOverlay
                                                role="button"
                                                tabIndex={0}
                                                css={css`
                                                    cursor: pointer;
                                                `}
                                                // 失敗した写真は拡大表示ではなく再アップロードを優先する
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    onRetryPhoto(photo.id)
                                                }}
                                                onKeyDown={e => {
                                                    if (
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                    ) {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        onRetryPhoto(photo.id)
                                                    }
                                                }}
                                            >
                                                {t(
                                                    "失敗\nタップで再アップロード"
                                                )}
                                            </StatusOverlay>
                                        )}
                                    </ThumbnailWrapper>
                                ))}
                            </ThumbnailGrid>
                        )}

                        <MultipleTextField
                            name="note"
                            label={t("備考（任意）")}
                            placeholder={t("特記事項があれば入力してください")}
                            value={note}
                            onChange={value => onChangeNote(value ?? "")}
                        />
                    </Card>
                </Content>
            </div>

            <ReportFooter {...layout.footerProps}>
                <Button
                    variant="filled"
                    color={canSubmit ? "primary" : undefined}
                    fullWidth
                    disabled={!canSubmit}
                    onClick={onConfirm}
                >
                    {t("確認画面へ")}
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onBack}
                    css={css`
                        margin-top: 8px;
                    `}
                >
                    {t("戻る")}
                </Button>
            </ReportFooter>

            <ImageViewerOverlay
                openedImage={openedImage}
                onClose={closeImageViewer}
            />
        </Main>
    )
}
